/* eslint-disable max-lines */
import { set_token, get_token, set_user_details, getCookie, csrf_headers } from "@/router";
import Swal from "sweetalert2";
import { get_profile_url } from "@/views/home/home_helper.js";
import { authFinaleSharedMethods } from "@/views/auth_finale_helper";

const PASSWORD_RESET_URL = process.env.VUE_APP_RESET_URL;
import { client_domain } from "@/main.js";

export default {
    inject: ['client_features'],
    data() {
        return ({
            email: '',
            password: '',
            login_type: 'individual',
            passwordVisible: false,
            reset_url: "",
            cbo_domain: false,
            dwypd_domain: false,
            ict_chamber: false,
            vettedsa_domain: false,
            bosa_domain: false,
            isSubmitting: false,
            workbook_domain: false,
            csrfToken: '',
        });
    },
    mounted() {
        this.reset_url = PASSWORD_RESET_URL;
        if (client_domain.includes('cbo')) this.cbo_domain = true;
        if (client_domain.includes('dwypd')) this.dwypd_domain = true;
        if (client_domain.includes('chamber')) this.ict_chamber = true;
        if (client_domain.includes('vettedsa')) this.vettedsa_domain = true;
        if (this.is_bosa_domain()) this.bosa_domain = true;
        if (client_domain.includes('workbook')) this.workbook_domain = true;
        this.csrfToken = getCookie('csrftoken');
    },
    methods: {
        ...authFinaleSharedMethods,
        togglePasswordVisibility() {
            this.passwordVisible = !this.passwordVisible;
        },

        async handleSubmit() {
            this.isSubmitting = true;
            this.clear_profile_modal_session();
            await this.ensure_csrf_cookie();

            $.ajax({
                type: "POST",
                url: `${process.env.VUE_APP_BACKEND_API_URL}/system_management/${this.get_login_endpoint()}/`,
                contentType: "application/json",
                xhrFields: { withCredentials: true },
                headers: {
                    'Content-Type': 'application/json',
                    ...csrf_headers(this.csrfToken),
                },
                data: JSON.stringify({
                    email: this.email.toLowerCase(),
                    password: this.password
                }),
                success: async (response) => {
                    await this.handle_login_success_response(response);
                },
                error: (xhr) => {
                    if (xhr?.status === 403) {
                        Swal.fire({
                            icon: 'error',
                            title: 'Forbidden (403)',
                            text: 'CSRF token missing/invalid or cookies not included. Please refresh and try again.',
                            position: 'bottom-end',
                            showConfirmButton: false,
                            timer: 3500,
                            toast: true
                        });
                        return;
                    }
                    const response = xhr.responseJSON;
                    if (response && response.status === "unverified") {
                        Swal.fire({
                            icon: 'warning',
                            title: 'Email Not Verified',
                            text: 'Please check your inbox and verify your email before logging in.',
                            position: 'bottom-end',
                            showConfirmButton: false,
                            timer: 3000,
                            toast: true
                        });
                    } else {
                        Swal.fire({
                            icon: 'error',
                            title: 'Incorrect Email or Password',
                            position: 'bottom-end',
                            showConfirmButton: false,
                            timer: 3000,
                            toast: true
                        });
                    }
                },
                complete: () => {
                    this.isSubmitting = false;
                }
            });
        },

        async handle_login_success_response(response) {
            if (response.status === "success") {
                this.show_login_success(response);
                return;
            }

            if (response.status === "created_user") {
                await this.handle_created_user_login(response);
            }
        },

        show_login_success(response) {
            Swal.fire({
                icon: 'success',
                title: 'Login Successful',
                showConfirmButton: false,
                timer: 1000,
                position: 'bottom-end',
                toast: true
            }).then(async () => {
                const response_data = response.data;

                this.store_login_session(response_data);
                this.send_flutter_auth_token(response.token);
                window.location = await this.get_success_login_next_url(response_data);
            });
        },

        async handle_created_user_login(response) {
            this.store_login_session(response);
            window.location = await this.get_created_user_next_url(response);
        },

        store_login_session(response_data) {
            set_token(response_data.token);
            const user_details = this.get_user_details_payload(response_data);
            set_user_details(user_details);
            this.set_active_organization_profile(response_data);
        },

        send_flutter_auth_token(token) {
            if (window.flutter_inappwebview) {
                window.flutter_inappwebview.callHandler("authToken", token);
            }
        },

        async get_success_login_next_url(response_data) {
            return this.get_matching_redirect_url(
                this.get_success_login_redirect_rules(response_data),
                sessionStorage.getItem('next') || response_data.redirect_url || '/home'
            );
        },

        async get_created_user_next_url(response) {
            return this.get_matching_redirect_url(
                this.get_created_user_redirect_rules(response),
                '/home'
            );
        },

        async get_matching_redirect_url(rules, fallback_url) {
            const matched_rule = rules.find((rule) => rule.matches());
            return matched_rule ? matched_rule.get_url() : fallback_url;
        },

        get_success_login_redirect_rules(response_data) {
            return [
                {
                    matches: () => this.is_first_time_login(this.get_response_user_details(response_data))
                        && !this.is_organization_admin_login(response_data),
                    get_url: () => '/first_password_change',
                },
                {
                    matches: () => this.is_bosa_domain()
                        && this.has_missing_required_profile_details(response_data.user_details),
                    get_url: () => this.get_profile_name_update_url(response_data.user_details),
                },
                {
                    matches: () => this.cbo_domain,
                    get_url: () => '/view_events',
                },
                {
                    matches: () => this.ict_chamber,
                    get_url: () => this.get_membership_next_url(),
                },
                {
                    matches: () => this.dwypd_domain,
                    get_url: () => '/knowledgehub',
                },
                {
                    matches: () => this.vettedsa_domain,
                    get_url: () => '/marketplace',
                },
            ];
        },

        get_created_user_redirect_rules(response) {
            return [
                {
                    matches: () => this.is_first_time_login(this.get_response_user_details(response))
                        && !this.is_organization_admin_login(response),
                    get_url: () => '/first_password_change',
                },
                {
                    matches: () => this.is_bosa_domain(),
                    get_url: () => this.get_bosa_next_url(response.user_details),
                },
                {
                    matches: () => this.client_features.marketplace,
                    get_url: () => '/marketplace',
                },
            ];
        },

        is_first_time_login(user_details) {
            const first_time_login = user_details?.first_time_login;
            return first_time_login === true || first_time_login === 'true' || first_time_login === 1 || first_time_login === '1';
        },

        get_response_user_details(response_data) {
            return response_data?.user_details || response_data?.data?.user_details || {};
        },

        is_organization_admin_login(response_data) {
            return this.client_features.show_organisation
                && this.is_organization_admin_user(response_data?.user_details);
        },

        is_organization_admin_user(user_details = {}) {
            const role = user_details?.role;
            const role_name = role?.role || '';
            return String(role_name).trim().toLowerCase() === 'organization admin';
        },

        get_login_endpoint() {
            return this.client_features.show_organisation? 'login_organization' : 'login';
        },

        async get_bosa_next_url(user_details) {
            const profile_data = await this.get_logged_in_user_profile(user_details);
            const required_details = this.build_profile_required_details(user_details, profile_data);

            if (this.has_missing_required_profile_details(required_details)) {
                return this.get_profile_name_update_url(user_details);
            }

            return '/home';
        },

        get_logged_in_user_profile(user_details) {
            return new Promise((resolve) => {
                $.ajax({
                    type: "GET",
                    url: `${process.env.VUE_APP_BACKEND_API_URL}/system_management/get_profile/`,
                    headers: {
                        Authorization: `Token ${get_token()}`,
                        'Content-Type': 'application/json',
                    },
                    data: { user_id: user_details?.id || user_details?.user_id || '' },
                    success: (response) => {
                        resolve(response?.data?.profile_data || null);
                    },
                    error: () => {
                        resolve(null);
                    }
                });
            });
        },

        build_profile_required_details(user_details, profile_data) {
            if (!profile_data) {
                return user_details;
            }

            return ({
                ...user_details,
                profile: {
                    ...user_details?.profile,
                    race: profile_data.race,
                    gender: profile_data.gender,
                    date_of_birth: profile_data.date_of_birth,
                    location: profile_data.location,
                }
            });
        },

        get_profile_name_update_url(user_details) {
            const user_id = user_details?.id || user_details?.user_id || '';
            this.set_profile_modal_session(user_details);
            return get_profile_url({
                id: user_id,
                first_name: user_details?.first_name,
                last_name: user_details?.last_name,
                profile_type: 'personal',
            }) || '/profile';
        },

        get_membership_next_url() {
            return new Promise((resolve) => {
                $.ajax({
                    type: "GET",
                    url: `${process.env.VUE_APP_BACKEND_API_URL}/membership_management/check_user_membership/`,
                    headers: {
                        Authorization: `Token ${get_token()}`,
                        'Content-Type': 'application/json',
                    },
                    success: (response) => {
                        let next_url = response.has_membership ? '/home' : '/view_membership';
                        resolve(next_url);
                    },
                    error: () => {
                        resolve('/home'); // fallback
                    }
                });
            });
        }
    }
};


